import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {forkJoin, Observable, of, throwError} from 'rxjs';
import {catchError, debounceTime, map, mergeMap, switchMap, take} from 'rxjs/operators';
import {
    actionEnterpriseAddedPrivilege,
    actionEnterpriseAddPrivilege,
    actionEnterpriseChangePassword,
    actionEnterpriseFailure,
    actionEnterpriseGeoIPsSuccess,
    actionEnterpriseRemovedPrivilege,
    actionEnterpriseRemovePrivilege,
    actionEnterpriseRequest, actionEnterpriseRequestSocketNames,
    actionEnterpriseSuccessA,
    actionEnterpriseSuccessB,
    actionEnterpriseSuccessC,
    actionEnterpriseSuccessD,
    actionEnterpriseStationsStatusV1V2tReply,
    actionEnterpriseStationV2NamesReply,
    actionEnterpriseUpdateApps,
    actionEnterpriseUpdateBusiness,
    actionEnterpriseUpdatedApps,
    actionEnterpriseUpdatedBusiness,
    actionEnterpriseUpdatedPrivilegeDefault,
    actionEnterpriseUpdatedUser,
    actionEnterpriseUpdatedWhitelabel,
    actionEnterpriseUpdatePrivilegeDefault,
    actionEnterpriseUpdatePrivilegeServer,
    actionEnterpriseUpdateUser,
    actionEnterpriseUpdateWhitelabel,
    actionEnterpriseRenameResellerUserName,
    actionEnterpriseRenameResellerUserNameReply, actionEnterpriseResetPreferences
} from '../actions/enterprise.actions';
import {ToastrService} from "ngx-toastr";
import {EnumEnterpriseServerRequest, IEnterpriseWhiteLabelUpdate, IPrivileges} from "../../interfaces/enterprise-interface";
import {EnterpriseService} from "../../containers/enterprise/services/enterprise-service";
import {IBusinessAccount} from "../../interfaces/enterprise-businesses-interface";
import {EnumIEnterpriseBusinessCommand, IEnterpriseApps, IEnterpriseBusinessUsers, IEnterpriseBusinessUserUpdate, IEnterprisePrivilegesUpdate} from "../../interfaces/enterprise-business-users-interface";
import {StoreSelectorsService} from "../../services";
import {Lib} from "../../../libs/Lib";
import * as _ from 'lodash';
import {IUser} from "../../interfaces";
import {actionNoop} from "../actions/user.actions";

@Injectable()
export class EnterpriseEffects {

    resetPreferences = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseResetPreferences),
            switchMap((action: any) => {
                    return this.enterpriseService.resetUserPreferences(action.user, action.businessId).pipe(
                        map((i_payload) => {
                            return i_payload.status === 'pass' ? actionNoop({}) : this.problemSavingAlert('Problem resetting preferences', null, false);
                        }),
                        catchError((i_error) => this.problemSavingAlert('Problem resetting preferences', i_error, true))
                    );
                }
            )
        )
    );

    updateAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseUpdateBusiness),
            map((action: { business: IBusinessAccount }) => action),
            switchMap((action: { business: IBusinessAccount }) => {
                    const msg = 'Problem updating account for business';
                    return this.enterpriseService.updateAccount(action.business).pipe(
                        map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedBusiness(action) : this.problemSavingAlert(msg, null, false)),
                        catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                    );
                }
            )
        )
    );

    updateApps$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseUpdateApps),
            map((action: IEnterpriseApps) => action),
            switchMap((action: IEnterpriseApps) => {
                    const msg = 'Problem updating apps';
                    return this.enterpriseService.updateApps(action).pipe(
                        map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedApps(action) : this.problemSavingAlert(msg, null, false)),
                        catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                    );
                }
            )
        )
    );

    updateWhiteLabel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseUpdateWhitelabel),
            map((action: IEnterpriseWhiteLabelUpdate) => action),
            debounceTime(1000),
            switchMap((action: IEnterpriseWhiteLabelUpdate) => {
                    const msg = 'Problem updating white label';
                    return this.enterpriseService.saveWhiteLabel(action).pipe(
                        map((i_payload) => i_payload['Studio'] ? actionEnterpriseUpdatedWhitelabel({whiteLabel: i_payload}) : this.problemSavingAlert(msg, null, false)),
                        catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                    );
                }
            )
        )
    );

    updatePassword$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseChangePassword),
            map((action: any) => action),
            debounceTime(1000),
            switchMap((action) => {
                    const msg = 'Problem changing password';
                    return this.enterpriseService.changePasswordBusinessUser(action.user, action.pass).pipe(
                        catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                    );
                }
            )
        ), {dispatch: false}
    );

    addPrivileges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseAddPrivilege),
            mergeMap((action: { enterprisePrivilegesUpdate: IEnterprisePrivilegesUpdate }) => {
                const msg = 'Problem adding privileges';
                return this.storeSelector.getEnterprisePrivileges().pipe(
                    take(1),
                    switchMap((privileges) => {
                        return this.enterpriseService.addPrivileges(action.enterprisePrivilegesUpdate, privileges).pipe(
                            map((i_payload) => {
                                action.enterprisePrivilegesUpdate.privilegeData = i_payload;
                                return i_payload ? actionEnterpriseAddedPrivilege({enterprisePrivilegesUpdate: action.enterprisePrivilegesUpdate}) : this.problemSavingAlert(msg, null, false);
                            }),
                            catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                        );
                    })
                );
            })
        )
    );

    savePriveleges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseUpdatePrivilegeServer),
            mergeMap((action: { enterprisePrivilegesUpdate: IEnterprisePrivilegesUpdate }) => {
                const msg = 'Problem updating privileges';
                return this.storeSelector.getEnterprisePrivileges().pipe(
                    take(1),
                    switchMap((privileges: Array<IPrivileges>) => {
                        return this.enterpriseService.savePrivileges(privileges, action.enterprisePrivilegesUpdate.privilegeId, action.enterprisePrivilegesUpdate.selPrivName).pipe(
                            map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? null : this.problemSavingAlert(msg, null, false)),
                            catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                        );
                    })
                );
            })
        ), {dispatch: false}
    );

    removePrivelege$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseRemovePrivilege),
            mergeMap((action: { enterprisePrivilegesUpdate: IEnterprisePrivilegesUpdate }) => {
                const msg = 'Problem updating privileges';
                return this.storeSelector.getEnterpriseBusinessUsers().pipe(
                    take(1),
                    switchMap((businessUsers) => {
                        return this.enterpriseService.removePrivileges(action.enterprisePrivilegesUpdate, businessUsers).pipe(
                            map((i_payload: string) => i_payload.toLocaleLowerCase() === '' ? actionEnterpriseRemovedPrivilege({enterprisePrivilegesUpdate: action.enterprisePrivilegesUpdate}) : this.problemSavingAlert(msg, null, false)),
                            catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                        );
                    })
                );
            })
        )
    );

    savePrivelegesDefault$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseUpdatePrivilegeDefault),
            mergeMap((action: { enterprisePrivilegesUpdate: IEnterprisePrivilegesUpdate }) => {
                const msg = 'Problem updating default privileges';
                return this.enterpriseService.updateDefaultPrivileges(action.enterprisePrivilegesUpdate).pipe(
                    map((i_payload: string) => i_payload.toLocaleLowerCase() === '' ? actionEnterpriseUpdatedPrivilegeDefault({enterprisePrivilegesUpdate: action.enterprisePrivilegesUpdate}) : this.problemSavingAlert(msg, null, false)),
                    catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                );
            })
        )
    );

    renameEnterpriseUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseRenameResellerUserName),
            mergeMap((action) => {
                const msg = 'Problem actionEnterpriseRenameResellerUserName';
                return this.enterpriseService.renameResellerUser(action.user, action.newUser).pipe(
                    map((i_payload: any) => {
                        return actionEnterpriseRenameResellerUserNameReply({status: i_payload === 'true'});
                    }),
                    catchError((i_error) => {
                        return of(actionEnterpriseRenameResellerUserNameReply({status: false}));
                    })
                );
            })
        )
    );

    updateBusinessUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseUpdateUser),
            map((action: IEnterpriseBusinessUserUpdate) => action),
            switchMap((action: IEnterpriseBusinessUserUpdate) => {
                    const i_command: EnumIEnterpriseBusinessCommand = action.command;
                    switch (i_command) {
                        case EnumIEnterpriseBusinessCommand.USER_CREATE_ACCOUNT_IMPORT: {
                            const msg = 'Problem importing account from server, maybe account is already under another enterprise user';
                            return this.enterpriseService.importAccount(action).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_CREATE_ACCOUNT_EMPTY: {
                            const msg = 'Problem creating empty account,maybe name is taken, pick another...';
                            action.businessId = action.businessId === '-1' ? '999' : '381610';
                            return this.enterpriseService.duplicateBusinessUserPriv(action).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_CREATE_ACCOUNT_SAMPLE: {
                            const msg = 'Problem creating account from template,maybe name is taken, pick another...';
                            return this.enterpriseService.duplicateBusinessUserPriv(action).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_UPDATE_ACCESS_PASSWORD: {
                            let msg;
                            msg = 'Problem updating business access list from server...1';
                            return forkJoin([
                                    this.enterpriseService.updateBusinessUserPriv(action.privilegeId, action.computedAccessMask, action.name2).pipe(
                                        map((i_payload: string) => i_payload === 'true' ? null : this.problemSavingAlert(msg, null, false)),
                                        catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                                    ), this.enterpriseService.changePasswordBusinessUser(action.name2, action.password).pipe(
                                        map((i_payload: string) => i_payload === 'true' ? null : this.problemSavingAlert(msg, null, false)),
                                        catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                                    )
                                ]
                            ).pipe(map((res) => {
                                return actionEnterpriseUpdatedUser(action);
                            }));
                        }
                        case EnumIEnterpriseBusinessCommand.USER_REMOVE: {
                            const msg = 'Problem removing business user from server...';
                            return this.enterpriseService.removeBusinessUser(action.name2).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_ACCOUNT: {
                            const msg = 'Problem removing account from server...';
                            return this.enterpriseService.removeBusinessAccount(action.businessId).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_UPDATE_ACCESS: {
                            /** workaround due to Chrome bug with multiple http calls in slow network, must subscribe with take(1) **/
                            this.enterpriseService.updateBusinessUserPriv(action.privilegeId, action.computedAccessMask, action.name2).pipe(
                                catchError((i_error) => this.problemSavingAlert('Problem updating access keys from server...', i_error, true))
                            ).pipe(take(1)).subscribe();
                            return of(actionEnterpriseUpdatedUser(action));

                            // return this.enterpriseService.updateBusinessUserPriv(action.privilegeId, action.computedAccessMask, action.name2).pipe(
                            //     map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                            //     catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            // );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_RENAME: {
                            const msg = 'Problem renaming user from, maybe name already exists...';
                            return this.enterpriseService.renameBusinessUser(action.name1, action.name2).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                        case EnumIEnterpriseBusinessCommand.USER_ADD: {
                            const msg = 'Problem adding user, maybe one already exists with the same name';
                            return this.enterpriseService.addEnterpriseBusinessUser(action).pipe(
                                map((i_payload: string) => i_payload.toLocaleLowerCase() === 'true' ? actionEnterpriseUpdatedUser(action) : this.problemSavingAlert(msg, null, false)),
                                catchError((i_error) => this.problemSavingAlert(msg, i_error, true))
                            );
                        }
                    }
                }
            )
        )
    );

    loadRequestEnterpriseSocketName$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseRequestSocketNames),
            map((action) => action),
            switchMap((action) => {
                    const i_args = action.args;
                    return this.enterpriseService.getEnterpriseStationSocketNames(i_args).pipe(
                        map((i_payload: any) => {
                            return actionEnterpriseStationV2NamesReply({stationArray: i_payload});
                        }),
                        catchError((i_error) => {
                            console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                            return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                        })
                    );
                }
            )
        )
    );

    loadRequestEnterpriseInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actionEnterpriseRequest),
            map((action) => action),
            switchMap((action) => {
                    const i_callType: EnumEnterpriseServerRequest = action.callType;
                    const i_args = action.args;
                    switch (i_callType) {
                        case EnumEnterpriseServerRequest.STATION_COLLECTION_GEOIP: {
                            return this.enterpriseService.getServerStationsGeoIPs().pipe(
                                map((i_payload: any) => {
                                    return actionEnterpriseGeoIPsSuccess({stationGeoIps: i_payload});
                                }),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }

                        case EnumEnterpriseServerRequest.STATION_DOMAIN_COLLECTION: {
                            if (_.size(i_args) === 0) {
                                console.log('no domains found...');
                                return of(actionEnterpriseStationsStatusV1V2tReply({stationArray: []}));
                            }
                            // Lib.DevShow(`total domains ${_.size(i_args)}`);
                            return this.enterpriseService.getEnterpriseStations(i_args).pipe(
                                map((i_payload: any) => actionEnterpriseStationsStatusV1V2tReply({stationArray: i_payload})),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }

                        /** for socket pwa v2 players only, update their names  **/
                        case EnumEnterpriseServerRequest.STATION_DOMAIN_COLLECTION_SOCKET_NAMES: {
                            if (_.size(i_args) === 0) console.log('no domains found for socket names...');
                            // Lib.DevShow(`station names total domains ${_.size(i_args)}`);
                            return this.enterpriseService.getEnterpriseStationSocketNames(i_args).pipe(
                                map((i_payload: any) => actionEnterpriseStationV2NamesReply({stationArray: i_payload})),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }

                        case EnumEnterpriseServerRequest.RESELLER_INFO: {
                            return this.enterpriseService.getEnterpriseReseller('GetBusinessUserInfo').pipe(
                                map((i_payload: any) => {
                                    /** append any new privilege tables that were hard coded to Lib xml template recently **/
                                    if (i_payload.User.BusinessInfo[0].Privileges[0] && i_payload.User.BusinessInfo[0].Privileges[0].Privilege.length > 0) {
                                        Lib.PrivilegesXmlTemplate(false, null, null, (err, template) => {
                                            if (err) throwError('problem parsing XML for privileges');
                                            template.Privilege.Groups[0].Group.forEach((v) => {
                                                const found = i_payload.User.BusinessInfo[0].Privileges[0].Privilege[0].Groups[0].Group.some((x) => x.$.name === v.$.name);
                                                if (!found) {
                                                    i_payload.User.BusinessInfo[0].Privileges[0].Privilege[0].Groups[0].Group.push(v);
                                                }
                                            });
                                        });
                                    }
                                    return actionEnterpriseSuccessA({
                                        callType: i_callType, enterprise: i_payload
                                    });
                                }),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }
                        case EnumEnterpriseServerRequest.ACCOUNT_DETAILS: {
                            return this.enterpriseService.getEnterpriseReseller('GetAccountInfo').pipe(
                                map((i_payload: any) => actionEnterpriseSuccessB({
                                    callType: i_callType, enterprise: i_payload
                                })),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }
                        case EnumEnterpriseServerRequest.BUSINESSES_INFO: {
                            return this.enterpriseService.getEnterpriseReseller('GetCustomers').pipe(
                                map((i_payload: any) => actionEnterpriseSuccessC({
                                    callType: i_callType, enterprise: i_payload
                                })),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }
                        case EnumEnterpriseServerRequest.BUSINESS_USERS: {
                            return this.enterpriseService.getEnterpriseBusinessUsers(i_args as Array<IBusinessAccount>).pipe(
                                map((i_payload: IEnterpriseBusinessUsers) => {
                                    return actionEnterpriseSuccessD({
                                        callType: i_callType, enterprise: i_payload
                                    });
                                }),
                                catchError((i_error) => {
                                    console.log(`loadRequestResellerInfo$ ${i_error.message}`);
                                    return of(actionEnterpriseFailure({errorMessage: i_error.message, enterprise: []}));
                                })
                            );
                        }
                    }
                }
            )
        )
    );

    constructor(private enterpriseService: EnterpriseService, private actions$: Actions, private toastr: ToastrService, private storeSelector: StoreSelectorsService) {
    }

    problemSavingAlert(msg, error, ofObs: boolean): Observable<any> | any {
        this.toastr.error(msg);
        console.log('problem remote server ' + error);
        if (ofObs) {
            return of(actionEnterpriseFailure({errorMessage: msg, enterprise: []}));
        } else {
            return actionEnterpriseFailure({errorMessage: msg, enterprise: []});
        }
    }
}
